/**
 * Property Tax Questionnaire - Guided form that maps answers to Schedule E
 * Data saves to properties + expenses, flows to tax forms via tax-1040 prepare
 * Progress is persisted so you can leave to look up expenses and return without losing your place.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import BackToTaxFormsBanner from '../components/BackToTaxFormsBanner';
import { Building, ArrowRight, ArrowLeft, FileText, CheckCircle, DollarSign, TrendingUp, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/apiClient';
import Disclaimer from '../components/Disclaimer';

const STORAGE_KEY = 'wealthcore_property_questionnaire';

// Full Schedule E Part I breakdown - all lines to fill the form completely
// First step is property selection (rentals must exist in Property Manager)
const SCHEDULE_E_FIELDS = [
  { key: 'property_select', label: 'Select rental property', scheduleELine: 'Line 1a', type: 'property_select' },
  { key: 'days_rented', label: 'Days rented at fair rental value', scheduleELine: 'Line 2', type: 'number', placeholder: '365' },
  { key: 'days_personal_use', label: 'Days of personal use', scheduleELine: 'Line 2', type: 'number', placeholder: '0' },
  { key: 'monthly_rent', label: 'Rents received (monthly × 12)', scheduleELine: 'Line 3', type: 'currency', placeholder: '0' },
  { key: 'advertising', label: 'Advertising (Line 5)', scheduleELine: 'Line 5', type: 'currency', placeholder: '0' },
  { key: 'auto_travel', label: 'Auto and travel (Line 6)', scheduleELine: 'Line 6', type: 'currency', placeholder: '0' },
  { key: 'cleaning_maintenance', label: 'Cleaning and maintenance (Line 7)', scheduleELine: 'Line 7', type: 'currency', placeholder: '0' },
  { key: 'commissions', label: 'Commissions (Line 8)', scheduleELine: 'Line 8', type: 'currency', placeholder: '0' },
  { key: 'insurance', label: 'Insurance (Line 9)', scheduleELine: 'Line 9', type: 'currency', placeholder: '0' },
  { key: 'legal_professional', label: 'Legal and professional fees (Line 10)', scheduleELine: 'Line 10', type: 'currency', placeholder: '0' },
  { key: 'management_fees', label: 'Management fees (Line 11)', scheduleELine: 'Line 11', type: 'currency', placeholder: '0' },
  { key: 'mortgage_interest', label: 'Mortgage interest to banks (Line 12)', scheduleELine: 'Line 12', type: 'currency', placeholder: '0' },
  { key: 'other_interest', label: 'Other interest (Line 13)', scheduleELine: 'Line 13', type: 'currency', placeholder: '0' },
  { key: 'repairs', label: 'Repairs (Line 14)', scheduleELine: 'Line 14', type: 'currency', placeholder: '0' },
  { key: 'supplies', label: 'Supplies (Line 15)', scheduleELine: 'Line 15', type: 'currency', placeholder: '0' },
  { key: 'property_taxes', label: 'Taxes (Line 16)', scheduleELine: 'Line 16', type: 'currency', placeholder: '0' },
  { key: 'utilities', label: 'Utilities (Line 17)', scheduleELine: 'Line 17', type: 'currency', placeholder: '0' },
  { key: 'purchase_price', label: 'Cost basis for depreciation (Line 18)', scheduleELine: 'Line 18', type: 'currency', placeholder: '0' },
  { key: 'rental_start_date', label: 'Date placed in service (Line 18)', scheduleELine: 'Line 18', type: 'date', placeholder: 'YYYY-MM-DD' },
  { key: 'other_expenses', label: 'Other expenses (Line 19)', scheduleELine: 'Line 19', type: 'currency', placeholder: '0' }
];

const PropertyTaxQuestionnaire = () => {
  const [searchParams] = useSearchParams();
  const yearFromUrl = searchParams.get('year');
  const [step, setStep] = useState(0);
  const [properties, setProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [taxYear, setTaxYear] = useState(() => {
    const y = yearFromUrl ? parseInt(yearFromUrl, 10) : new Date().getFullYear();
    return (y >= 2020 && y <= 2030) ? y : new Date().getFullYear();
  });

  // Persist progress so you can leave to look up expenses and return without losing your place
  const saveProgress = useCallback(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        step,
        formData,
        taxYear,
        selectedPropertyId,
        savedAt: Date.now()
      }));
    } catch (e) { /* ignore */ }
  }, [step, formData, taxYear, selectedPropertyId]);

  useEffect(() => {
    saveProgress();
  }, [saveProgress]);

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      const res = await api.get('/api/properties');
      const allProps = res.data?.success && res.data?.data ? res.data.data : [];
      const rentals = allProps.filter(p => p.type === 'rental');
      setProperties(allProps);

      let initialFormData = {};
      let initialStep = 0;
      let initialSelectedId = null;

      if (rentals.length > 0) {
        // Restore progress from sessionStorage
        try {
          const stored = sessionStorage.getItem(STORAGE_KEY);
          if (stored) {
            const parsed = JSON.parse(stored);
            const age = Date.now() - (parsed.savedAt || 0);
            if (age < 7 * 24 * 60 * 60 * 1000) {
              initialStep = Math.min(parsed.step ?? 0, SCHEDULE_E_FIELDS.length - 1);
              const restoredId = parsed.selectedPropertyId;
              if (restoredId && rentals.some(r => r.id === restoredId)) {
                initialSelectedId = restoredId;
              }
              if (parsed.formData && Object.keys(parsed.formData).length > 0) {
                initialFormData = { ...initialFormData, ...parsed.formData };
              }
              if (parsed.taxYear && parsed.taxYear >= 2020 && parsed.taxYear <= 2030) {
                setTaxYear(parsed.taxYear);
              }
            }
          }
        } catch (e) { /* ignore */ }

        // If no restored selection, use first rental
        if (!initialSelectedId) initialSelectedId = rentals[0].id;
        const selectedRental = rentals.find(r => r.id === initialSelectedId) || rentals[0];
        initialFormData = {
          ...initialFormData,
          name: selectedRental.name,
          address: selectedRental.address,
          city: selectedRental.city,
          state: selectedRental.state,
          zip_code: selectedRental.zip_code,
          monthly_rent: selectedRental.monthly_rent || '',
          monthly_expenses: selectedRental.monthly_expenses || '',
          purchase_price: selectedRental.purchase_price || '',
          rental_start_date: selectedRental.rental_start_date ? String(selectedRental.rental_start_date).slice(0, 10) : '',
          days_rented: initialFormData.days_rented ?? '365',
          days_personal_use: initialFormData.days_personal_use ?? '0',
          insurance: initialFormData.insurance ?? '',
          mortgage_interest: initialFormData.mortgage_interest ?? '',
          repairs: initialFormData.repairs ?? '',
          property_taxes: initialFormData.property_taxes ?? '',
          utilities: initialFormData.utilities ?? '',
          advertising: initialFormData.advertising ?? '',
          auto_travel: initialFormData.auto_travel ?? '',
          cleaning_maintenance: initialFormData.cleaning_maintenance ?? '',
          commissions: initialFormData.commissions ?? '',
          legal_professional: initialFormData.legal_professional ?? '',
          management_fees: initialFormData.management_fees ?? '',
          other_interest: initialFormData.other_interest ?? '',
          supplies: initialFormData.supplies ?? '',
          other_expenses: initialFormData.other_expenses ?? ''
        };
        setSelectedPropertyId(initialSelectedId);
      }
      setFormData(initialFormData);
      setStep(initialStep);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handlePropertySelect = (propertyId) => {
    const id = propertyId ? parseInt(propertyId, 10) : null;
    setSelectedPropertyId(id);
    if (id) {
      const rental = properties.find(p => p.id === id && p.type === 'rental');
      if (rental) {
        setFormData(prev => ({
          ...prev,
          name: rental.name,
          address: rental.address,
          city: rental.city,
          state: rental.state,
          zip_code: rental.zip_code,
          monthly_rent: rental.monthly_rent || '',
          purchase_price: rental.purchase_price || '',
          rental_start_date: rental.rental_start_date ? String(rental.rental_start_date).slice(0, 10) : ''
        }));
      }
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const rental = selectedPropertyId ? properties.find(p => p.id === selectedPropertyId && p.type === 'rental') : null;
      if (!rental) {
        toast.error('Please select a rental property first');
        setSaving(false);
        return;
      }
      const mortgageAmt = parseFloat(formData.mortgage_interest || 0) || 0;
      const totalExpenses = (parseFloat(formData.advertising || 0) + parseFloat(formData.auto_travel || 0) + parseFloat(formData.cleaning_maintenance || 0) + parseFloat(formData.commissions || 0) + parseFloat(formData.insurance || 0) + parseFloat(formData.legal_professional || 0) + parseFloat(formData.management_fees || 0) + mortgageAmt + parseFloat(formData.other_interest || 0) + parseFloat(formData.repairs || 0) + parseFloat(formData.supplies || 0) + parseFloat(formData.property_taxes || 0) + parseFloat(formData.utilities || 0) + parseFloat(formData.other_expenses || 0));
      const basePayload = {
        name: formData.name || 'Rental Property',
        type: 'rental',
        address: (formData.address || formData.name || 'Rental Property').trim() || 'Rental Property',
        city: (formData.city || 'N/A').trim() || 'N/A',
        state: (formData.state || 'NA').trim().slice(0, 2) || 'NA',
        zip_code: (formData.zip_code || '00000').trim().slice(0, 10) || '00000',
        has_mortgage: mortgageAmt > 0 ? 'yes' : 'no',
        is_usa: 'yes',
        monthly_rent: parseFloat(formData.monthly_rent) || 0,
        monthly_expenses: totalExpenses / 12,
        purchase_price: parseFloat(formData.purchase_price) || null,
        rental_start_date: formData.rental_start_date || null
      };
      const payload = { ...rental, ...basePayload };
      const propertyId = rental.id;
      await api.put(`/api/properties/${rental.id}`, payload);
      // Save expenses for Schedule E (full breakdown - tax-1040 prepare pulls from expenses table)
      const expenseCategories = [
        { category: 'advertising', amount: parseFloat(formData.advertising || 0), desc: 'Advertising (Line 5)' },
        { category: 'travel', amount: parseFloat(formData.auto_travel || 0), desc: 'Auto and travel (Line 6)' },
        { category: 'maintenance', amount: parseFloat(formData.cleaning_maintenance || 0), desc: 'Cleaning and maintenance (Line 7)' },
        { category: 'commissions', amount: parseFloat(formData.commissions || 0), desc: 'Commissions (Line 8)' },
        { category: 'insurance', amount: parseFloat(formData.insurance || 0), desc: 'Insurance (Line 9)' },
        { category: 'professional_services', amount: parseFloat(formData.legal_professional || 0), desc: 'Legal and professional (Line 10)' },
        { category: 'property_management', amount: parseFloat(formData.management_fees || 0), desc: 'Management fees (Line 11)' },
        { category: 'mortgage', amount: parseFloat(formData.mortgage_interest || 0), desc: 'Mortgage interest (Line 12)' },
        { category: 'other_interest', amount: parseFloat(formData.other_interest || 0), desc: 'Other interest (Line 13)' },
        { category: 'repairs', amount: parseFloat(formData.repairs || 0), desc: 'Repairs (Line 14)' },
        { category: 'supplies', amount: parseFloat(formData.supplies || 0), desc: 'Supplies (Line 15)' },
        { category: 'property_taxes', amount: parseFloat(formData.property_taxes || 0), desc: 'Property taxes (Line 16)' },
        { category: 'utilities', amount: parseFloat(formData.utilities || 0), desc: 'Utilities (Line 17)' },
        { category: 'other', amount: parseFloat(formData.other_expenses || 0), desc: 'Other expenses (Line 19)' }
      ];
      const dateStr = `${taxYear}-01-01`;
      for (const exp of expenseCategories) {
        if (exp.amount > 0 && propertyId) {
          await api.post('/api/expenses', {
            category: exp.category,
            description: exp.desc,
            amount: exp.amount,
            date: dateStr,
            property_id: propertyId,
            tax_year: taxYear,
            is_recurring: 0
          }).catch(() => {});
        }
      }
      toast.success('Saved! Data will appear on your tax forms.');
      setStep(SCHEDULE_E_FIELDS.length);
      try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
    } catch (e) {
      const msg = e.response?.data?.message || e.response?.data?.error || e.message || 'Failed to save';
      toast.error(msg);
      console.error('Questionnaire save error:', e.response?.data || e);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  const currentField = SCHEDULE_E_FIELDS[step];
  const isLastStep = step >= SCHEDULE_E_FIELDS.length - 1;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <BackToTaxFormsBanner />
      {/* Breadcrumb - matches Tax Forms */}
      <nav className="mb-4 text-sm text-gray-500">
        <Link to="/dashboard" className="hover:text-gray-700">Dashboard</Link>
        <span className="mx-2">/</span>
        <Link to="/tax-forms" className="hover:text-gray-700">Tax Forms</Link>
        <span className="mx-2">/</span>
        <span className="text-gray-900 font-medium">Rental Questionnaire</span>
      </nav>
      <div className="mb-6 flex flex-wrap gap-3">
        <Link to="/tax-forms" className="text-blue-600 hover:underline flex items-center gap-1 text-sm">
          <ArrowLeft className="h-4 w-4" /> Back to Tax Forms
        </Link>
        <Link to="/property-manager" className="text-gray-500 hover:text-gray-700 flex items-center gap-1 text-sm">
          Property Manager
        </Link>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
            Schedule E – Rental Questionnaire
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Your answers flow into Tax Forms. Tax year: <strong>{taxYear}</strong>
          </p>
        </div>
        <select
          value={taxYear}
          onChange={(e) => setTaxYear(parseInt(e.target.value, 10))}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm"
        >
          {[2024, 2025, 2026].map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>
      <Disclaimer variant="compact" className="mb-6" />

      {/* Add Data bar - open in new tab so you don't lose your place */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg py-3 px-4 mb-6 flex flex-wrap items-center gap-2 sm:gap-4 text-sm">
        <span className="font-medium text-blue-800">Look up amounts without losing your place:</span>
        <div className="flex flex-wrap gap-2">
          <a href="/expense-tracker" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-blue-700 hover:text-blue-900 hover:underline">
            <DollarSign className="h-3.5 w-3.5" />
            Expense Tracker <ExternalLink className="h-3 w-3" />
          </a>
          <span className="text-blue-300">|</span>
          <a href="/income-tracker" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-blue-700 hover:text-blue-900 hover:underline">
            <TrendingUp className="h-3.5 w-3.5" />
            Income Tracker <ExternalLink className="h-3 w-3" />
          </a>
          <span className="text-blue-300">|</span>
          <a href="/property-manager" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-blue-700 hover:text-blue-900 hover:underline">
            <Building className="h-3.5 w-3.5" />
            Property Manager <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>

      {(() => {
        const rentals = properties.filter(p => p.type === 'rental');
        if (rentals.length === 0) {
          return (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
              <Building className="h-12 w-12 text-amber-600 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-amber-900">Add a rental property first</h3>
              <p className="text-amber-800 mt-2">Rental properties must be added in Property Manager before you can complete the Schedule E questionnaire.</p>
              <Link
                to="/property-manager"
                className="inline-flex items-center mt-4 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
              >
                Go to Property Manager <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </div>
          );
        }
        return null;
      })()}

      {step < SCHEDULE_E_FIELDS.length && properties.filter(p => p.type === 'rental').length > 0 ? (
        <div className="bg-white rounded-lg shadow border p-6">
          <div className="mb-4 text-sm text-blue-600 font-medium">
            {currentField.scheduleELine}
          </div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {currentField.label}
          </label>
          {currentField.type === 'property_select' ? (
            <select
              value={selectedPropertyId ?? ''}
              onChange={(e) => handlePropertySelect(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
            >
              <option value="">— Select a rental property —</option>
              {properties.filter(p => p.type === 'rental').map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name || p.address || `Property ${p.id}`}
                </option>
              ))}
            </select>
          ) : currentField.type === 'currency' ? (
            <input
              type="number"
              step="0.01"
              value={formData[currentField.key] ?? ''}
              onChange={e => handleChange(currentField.key, e.target.value)}
              placeholder={currentField.placeholder}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
            />
          ) : currentField.type === 'number' ? (
            <input
              type="number"
              min="0"
              max="365"
              value={formData[currentField.key] ?? ''}
              onChange={e => handleChange(currentField.key, e.target.value)}
              placeholder={currentField.placeholder}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
            />
          ) : currentField.type === 'date' ? (
            <input
              type="date"
              value={formData[currentField.key] ?? ''}
              onChange={e => handleChange(currentField.key, e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
            />
          ) : (
            <input
              type="text"
              value={formData[currentField.key] ?? ''}
              onChange={e => handleChange(currentField.key, e.target.value)}
              placeholder={currentField.placeholder}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
            />
          )}
          <div className="flex justify-between mt-6">
            <button
              onClick={() => setStep(s => Math.max(0, s - 1))}
              disabled={step === 0}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <ArrowLeft className="h-4 w-4 inline mr-1" /> Back
            </button>
            {isLastStep ? (
              <button
                onClick={handleSubmit}
                disabled={saving || !selectedPropertyId}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Submit to Tax Forms'}
              </button>
            ) : (
              <button
                onClick={() => setStep(s => s + 1)}
                disabled={currentField.type === 'property_select' && !selectedPropertyId}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Next <ArrowRight className="h-4 w-4 inline ml-1" />
              </button>
            )}
          </div>
          <div className="mt-4 text-xs text-gray-500">
            Step {step + 1} of {SCHEDULE_E_FIELDS.length}
          </div>
        </div>
      ) : (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-green-800">All set!</h2>
          <p className="text-green-700 mt-2">Your rental data is saved and will appear on your tax forms.</p>
          <div className="flex flex-wrap justify-center gap-3 mt-4">
            <Link
              to="/tax-forms"
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              View Tax Forms <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
            <Link
              to="/tax-forms"
              className="inline-flex items-center px-4 py-2 bg-white border border-green-600 text-green-700 rounded-lg hover:bg-green-50"
            >
              Go to Tax Forms → Export PDF
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyTaxQuestionnaire;
